import {
  differenceInCalendarDays,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  isWithinInterval,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subDays,
} from 'date-fns'
import omit from 'lodash-es/omit'

import { HTTP_STATUS_INTERNAL_SERVER_ERROR } from '../../../constants/api'
import { EMPTY_LIST, EMPTY_STRING } from '../../../constants/common'
import { DATE_API_DATA_FORMAT_SHORT } from '../../../constants/date'
import {
  RENTAL_PORTAL_LISTING_STATUS,
  RENTAL_PORTAL_VIEWING_BOOKED_STATUSES,
  RENTAL_PORTAL_VIEWING_STATUS,
} from '../../../constants/rentalPortal'
import {
  RENTAL_PORTAL_MOCK_LATENCY,
  RENTAL_PORTAL_MOCK_STATE,
} from '../../../constants/rentalPortalMockState'
import {
  APPLICANT_DETAILS_SNAPSHOT,
  APPLICANTS_SNAPSHOT,
  EMAILS_SNAPSHOT,
  LISTINGS_SNAPSHOT,
  PORTAL_PUBLICATIONS_SNAPSHOT,
  VIEWINGS_SNAPSHOT,
} from '../../../utils/rentalPortalMockData'
import {
  getRentalPortalMockAvatarColor,
  getRentalPortalMockMediaUrl,
} from '../../../utils/rentalPortalMockMedia'
import { hasRentalPortalMockState } from '../../../utils/rentalPortalMockState'
import { APPLICANT_STAGE } from '../../RentalPortalApplicant'
import { LISTING_PORTAL_SYNC_STATUS } from '../../RentalPortalListing'

import {
  OVERVIEW_ATTENTION_REASON_KEY_BY_TYPE,
  OVERVIEW_ATTENTION_SEVERITY,
  OVERVIEW_ATTENTION_TYPE,
  OVERVIEW_PAYLOAD_FIELD,
  OVERVIEW_PERIOD,
  OVERVIEW_PERIOD_DEFAULT,
  OVERVIEW_PERIOD_LIST,
} from './RentalPortalOverview.constants'

const MOCK_OVERVIEW_READ_LATENCY = 300

const MOCK_OVERVIEW_READ_ERROR = {
  response: { status: HTTP_STATUS_INTERNAL_SERVER_ERROR },
}

const MOCK_OVERVIEW_SESSION_USER_ID = 77

const MOCK_OVERVIEW_WEEK_OPTIONS = { weekStartsOn: 1 }

const MOCK_OVERVIEW_LAST_30_DAYS = 30

const MOCK_OVERVIEW_ISO_DAY_START = 'T00:00:00'

const MOCK_OVERVIEW_INBOUND_DIRECTION = 'inbound'

const MOCK_OVERVIEW_CAN_APPROVE_FIELD = 'can_approve'

const MOCK_OVERVIEW_PANEL_ROW_LIMIT = 5

// TODO: §11 caps every panel at five rows and declares no total, so the header
// counter is served from the mock's own pre-cap length until the endpoint sends
// one per panel.
function toMockOverviewPanel({
  limit = MOCK_OVERVIEW_PANEL_ROW_LIMIT,
  rows,
  toRow,
}) {
  return { rows: rows.slice(0, limit).map(toRow), total: rows.length }
}

const MOCK_OVERVIEW_UNEVEN_PANEL_ROW_LIMIT = 1

const MOCK_OVERVIEW_ATTENTION_STALE_DAYS = 14

const MOCK_OVERVIEW_ATTENTION_TYPE_RANKING = [
  OVERVIEW_ATTENTION_TYPE.viewingOutcomeMissing,
  OVERVIEW_ATTENTION_TYPE.newApplicants,
  OVERVIEW_ATTENTION_TYPE.portalError,
  OVERVIEW_ATTENTION_TYPE.stale,
]

const MOCK_OVERVIEW_ATTENTION_SEVERITY_BY_TYPE = {
  [OVERVIEW_ATTENTION_TYPE.newApplicants]: OVERVIEW_ATTENTION_SEVERITY.amber,
  [OVERVIEW_ATTENTION_TYPE.portalError]: OVERVIEW_ATTENTION_SEVERITY.red,
  [OVERVIEW_ATTENTION_TYPE.stale]: OVERVIEW_ATTENTION_SEVERITY.neutral,
  [OVERVIEW_ATTENTION_TYPE.viewingOutcomeMissing]:
    OVERVIEW_ATTENTION_SEVERITY.red,
}

const MOCK_OVERVIEW_MASKED_APPLICANT_DISPLAY = '—'

const MOCK_OVERVIEW_BIG_KPIS = {
  active_listings: 1234,
  applicants_in_screening: 12345,
  new_applicants: 999,
  viewings_this_week: 1000,
}

const MOCK_OVERVIEW_MASKING_DEFECT_STATE =
  RENTAL_PORTAL_MOCK_STATE.maskingDefect

const MOCK_OVERVIEW_APPLICANT_DETAIL_BY_ID = new Map(
  APPLICANT_DETAILS_SNAPSHOT.records.map(record => [record.id, record])
)

const MOCK_OVERVIEW_APPLICANT_BY_ID = new Map(
  APPLICANTS_SNAPSHOT.map(applicant => [applicant.id, applicant])
)

function getMockOverviewHasScope() {
  return !hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.noUnits)
}

function getIsMockOverviewEmpty() {
  return (
    hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.empty) ||
    !getMockOverviewHasScope()
  )
}

// TODO: `has_scope` is not in §11 — without it "no funnel data" and "no scope at
// all" are the same all-zero payload and only one of the two Screen A empty
// states can be rendered. The field is owed by the endpoint.
// An archived listing keeps its `→ closed` transition in the historical count
// (§9) while leaving every current-state counter and every panel, hence the two
// lists.
function getMockOverviewOwnedListings() {
  return getIsMockOverviewEmpty()
    ? EMPTY_LIST
    : LISTINGS_SNAPSHOT.filter(
        listing =>
          listing.responsible_user?.id === MOCK_OVERVIEW_SESSION_USER_ID
      )
}

function toMockOverviewScope() {
  const ownedListings = getMockOverviewOwnedListings()
  const listings = ownedListings.filter(listing => !listing.archived)

  return {
    listingById: new Map(listings.map(listing => [listing.id, listing])),
    listings,
    ownedListings,
  }
}

function toMockOverviewClock() {
  const now = new Date()
  const today = startOfDay(now)

  return { now, today }
}

function toMockOverviewWeekInterval(clock) {
  return {
    end: endOfWeek(clock.today, MOCK_OVERVIEW_WEEK_OPTIONS),
    start: startOfWeek(clock.today, MOCK_OVERVIEW_WEEK_OPTIONS),
  }
}

function toMockOverviewPeriodInterval(period, clock) {
  if (period === OVERVIEW_PERIOD.thisMonth) {
    return { end: endOfMonth(clock.today), start: startOfMonth(clock.today) }
  }

  if (period === OVERVIEW_PERIOD.last30) {
    return {
      end: endOfDay(clock.today),
      start: subDays(clock.today, MOCK_OVERVIEW_LAST_30_DAYS - 1),
    }
  }

  return toMockOverviewWeekInterval(clock)
}

function getMockOverviewScopeApplicants(scope) {
  return APPLICANTS_SNAPSHOT.filter(
    applicant =>
      scope.listingById.has(applicant.listing_id) && !applicant.archived
  )
}

function getMockOverviewScopeViewings(scope) {
  return VIEWINGS_SNAPSHOT.filter(
    viewing => scope.listingById.has(viewing.listing_id) && !viewing.archived
  )
}

function getMockOverviewClosedListingDate(listing) {
  return parseISO(listing.closed_at)
}

function getMockOverviewApplicantReceivedDate(applicant) {
  return parseISO(`${applicant.received}${MOCK_OVERVIEW_ISO_DAY_START}`)
}

function getMockOverviewViewingDate(viewing) {
  return parseISO(viewing.start)
}

function getMockOverviewKpis({ clock, period, scope }) {
  const applicants = getMockOverviewScopeApplicants(scope)
  const interval = toMockOverviewPeriodInterval(period, clock)
  const week = toMockOverviewWeekInterval(clock)

  const kpis = {
    active_listings: scope.listings.filter(
      listing => listing.status === RENTAL_PORTAL_LISTING_STATUS.published
    ).length,
    applicants_in_screening: applicants.filter(
      applicant => applicant.stage === APPLICANT_STAGE.screening
    ).length,
    listings_closed: scope.ownedListings.filter(
      listing =>
        listing.status === RENTAL_PORTAL_LISTING_STATUS.closed &&
        isWithinInterval(getMockOverviewClosedListingDate(listing), interval)
    ).length,
    new_applicants: applicants.filter(
      applicant =>
        applicant.stage === APPLICANT_STAGE.pool &&
        isWithinInterval(
          getMockOverviewApplicantReceivedDate(applicant),
          interval
        )
    ).length,
    viewings_this_week: getMockOverviewScopeViewings(scope).filter(
      viewing =>
        RENTAL_PORTAL_VIEWING_BOOKED_STATUSES.includes(viewing.status) &&
        isWithinInterval(getMockOverviewViewingDate(viewing), week)
    ).length,
  }

  return hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.bigCounts)
    ? { ...kpis, ...MOCK_OVERVIEW_BIG_KPIS }
    : kpis
}

// TODO: a fixture — the real figure is the same count over the previous period,
// which the reporting endpoint does not return yet.
const MOCK_OVERVIEW_KPI_DELTA = {
  active_listings: 1,
  applicants_in_screening: 4,
  listings_closed: 1,
  new_applicants: -2,
  viewings_this_week: 3,
}

function getMockOverviewKpiDelta() {
  return { ...MOCK_OVERVIEW_KPI_DELTA }
}

function getMockOverviewApprovalCandidates(scope) {
  return getMockOverviewScopeApplicants(scope)
    .filter(applicant => applicant.stage === APPLICANT_STAGE.selected)
    .map(applicant => ({
      applicant,
      detail: MOCK_OVERVIEW_APPLICANT_DETAIL_BY_ID.get(applicant.id),
    }))
    .filter(({ detail }) => detail?.selected_at)
    .map(({ applicant, detail }) => ({
      applicant,
      selectedAt: detail.selected_at,
      selectedByUserId: detail.selected_by_user_id,
    }))
    .sort(
      (left, right) =>
        left.selectedAt.localeCompare(right.selectedAt) ||
        left.applicant.id - right.applicant.id
    )
}

function toMockOverviewSelectorCandidates(candidates) {
  return hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.viewerIsSelector)
    ? candidates.map((candidate, index) =>
        index === 0
          ? { ...candidate, selectedByUserId: MOCK_OVERVIEW_SESSION_USER_ID }
          : candidate
      )
    : candidates
}

function toMockOverviewApprovalRow({ canApprove, candidate, index, scope }) {
  const row = {
    applicant_color: getRentalPortalMockAvatarColor(candidate.applicant.id),
    applicant_display: candidate.applicant.identity?.value,
    applicant_id: candidate.applicant.id,
    can_approve: canApprove,
    flat: scope.listingById.get(candidate.applicant.listing_id)?.flat,
    listing_id: candidate.applicant.listing_id,
    selected_at: candidate.selectedAt,
    sort_rank: index + 1,
  }

  return hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.reducedManager) &&
    index % 2 === 1
    ? omit(row, MOCK_OVERVIEW_CAN_APPROVE_FIELD)
    : row
}

function getMockOverviewAwaitingApproval(scope) {
  const hasApproverGrant = !hasRentalPortalMockState(
    RENTAL_PORTAL_MOCK_STATE.noApprovalGrant
  )
  const candidates = toMockOverviewSelectorCandidates(
    getMockOverviewApprovalCandidates(scope)
  ).filter(
    candidate =>
      !hasApproverGrant ||
      candidate.selectedByUserId !== MOCK_OVERVIEW_SESSION_USER_ID
  )

  return toMockOverviewPanel({
    limit: hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.unevenPanels)
      ? MOCK_OVERVIEW_UNEVEN_PANEL_ROW_LIMIT
      : MOCK_OVERVIEW_PANEL_ROW_LIMIT,
    rows: candidates,
    toRow: (candidate, index) =>
      toMockOverviewApprovalRow({
        canApprove: hasApproverGrant,
        candidate,
        index,
        scope,
      }),
  })
}

// TODO: `viewing_id` is not in §11 — without it a `viewing_outcome_missing` row
// cannot address the viewing whose outcome is missing, which AC-RP08-09
// requires. The field is owed by the endpoint, `null` for every other type
//.
function getMockOverviewOutcomeMissingRows({ clock, scope }) {
  const rows = new Map()

  getMockOverviewScopeViewings(scope)
    .filter(
      viewing =>
        viewing.status === RENTAL_PORTAL_VIEWING_STATUS.confirmed &&
        getMockOverviewViewingDate(viewing) < clock.now
    )
    .sort(
      (left, right) =>
        getMockOverviewViewingDate(left) - getMockOverviewViewingDate(right)
    )
    .forEach(viewing => {
      if (!rows.has(viewing.listing_id)) {
        rows.set(viewing.listing_id, {
          count: null,
          days: null,
          listingId: viewing.listing_id,
          timestamp: getMockOverviewViewingDate(viewing),
          type: OVERVIEW_ATTENTION_TYPE.viewingOutcomeMissing,
          viewingId: viewing.id,
        })
      }
    })

  return [...rows.values()]
}

function getMockOverviewNewApplicantRows(scope) {
  const rows = new Map()

  getMockOverviewScopeApplicants(scope)
    .filter(
      applicant => applicant.stage === APPLICANT_STAGE.pool && applicant.unseen
    )
    .forEach(applicant => {
      const received = getMockOverviewApplicantReceivedDate(applicant)
      const row = rows.get(applicant.listing_id)

      rows.set(applicant.listing_id, {
        count: (row?.count ?? 0) + 1,
        days: null,
        listingId: applicant.listing_id,
        timestamp:
          row?.timestamp && row.timestamp < received ? row.timestamp : received,
        type: OVERVIEW_ATTENTION_TYPE.newApplicants,
      })
    })

  return [...rows.values()]
}

function getMockOverviewPortalErrorRows(scope) {
  return scope.listings
    .filter(
      listing => listing.status === RENTAL_PORTAL_LISTING_STATUS.published
    )
    .map(listing => ({
      listing,
      publication: (
        PORTAL_PUBLICATIONS_SNAPSHOT[String(listing.id)] ?? EMPTY_LIST
      ).find(row => row.sync_status === LISTING_PORTAL_SYNC_STATUS.error),
    }))
    .filter(({ publication }) => publication)
    .map(({ listing, publication }) => ({
      count: null,
      days: null,
      listingId: listing.id,
      timestamp: parseISO(publication.last_action_at),
      type: OVERVIEW_ATTENTION_TYPE.portalError,
    }))
}

function getMockOverviewStaleListings(scope) {
  const published = scope.listings.filter(
    listing =>
      listing.status === RENTAL_PORTAL_LISTING_STATUS.published &&
      listing.first_published_at
  )

  return hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.unevenPanels)
    ? published
        .slice()
        .sort((left, right) =>
          left.first_published_at.localeCompare(right.first_published_at)
        )
        .slice(0, 1)
    : published.filter(
        listing =>
          !APPLICANTS_SNAPSHOT.some(
            applicant =>
              applicant.listing_id === listing.id && !applicant.archived
          )
      )
}

function getMockOverviewStaleRows({ clock, scope }) {
  return getMockOverviewStaleListings(scope)
    .map(listing => ({
      listing,
      publishedAt: parseISO(listing.first_published_at),
    }))
    .map(({ listing, publishedAt }) => ({
      count: null,
      days: differenceInCalendarDays(clock.today, publishedAt),
      listingId: listing.id,
      timestamp: publishedAt,
      type: OVERVIEW_ATTENTION_TYPE.stale,
    }))
    .filter(row => row.days >= MOCK_OVERVIEW_ATTENTION_STALE_DAYS)
}

function toMockOverviewAttentionRow({ index, row, scope }) {
  const listing = scope.listingById.get(row.listingId)

  return {
    avatar_url: getRentalPortalMockMediaUrl(listing?.cover_photo?.thumb_url),
    count: row.count,
    days: row.days,
    flat: listing?.flat,
    listing_id: row.listingId,
    reason_key: OVERVIEW_ATTENTION_REASON_KEY_BY_TYPE[row.type],
    severity: MOCK_OVERVIEW_ATTENTION_SEVERITY_BY_TYPE[row.type],
    sort_rank: index + 1,
    type: row.type,
    viewing_id: row.viewingId ?? null,
  }
}

function toMockOverviewTypeSpreadRows(rows) {
  const leading = MOCK_OVERVIEW_ATTENTION_TYPE_RANKING.map(type =>
    rows.find(row => row.type === type)
  ).filter(Boolean)

  return [...leading, ...rows.filter(row => !leading.includes(row))]
}

function getMockOverviewNeedsAttention({ clock, scope }) {
  const rows = [
    ...getMockOverviewOutcomeMissingRows({ clock, scope }),
    ...getMockOverviewNewApplicantRows(scope),
    ...getMockOverviewPortalErrorRows(scope),
    ...getMockOverviewStaleRows({ clock, scope }),
  ].sort(
    (left, right) =>
      MOCK_OVERVIEW_ATTENTION_TYPE_RANKING.indexOf(left.type) -
        MOCK_OVERVIEW_ATTENTION_TYPE_RANKING.indexOf(right.type) ||
      left.timestamp - right.timestamp ||
      left.listingId - right.listingId
  )

  return toMockOverviewPanel({
    rows: hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.unevenPanels)
      ? toMockOverviewTypeSpreadRows(rows)
      : rows,
    toRow: (row, index) => toMockOverviewAttentionRow({ index, row, scope }),
  })
}

function getIsMockOverviewRevealedSender(applicant) {
  return (
    hasRentalPortalMockState(MOCK_OVERVIEW_MASKING_DEFECT_STATE) ||
    applicant.stage !== APPLICANT_STAGE.pool
  )
}

function getIsMockOverviewPoolSender(email) {
  return (
    MOCK_OVERVIEW_APPLICANT_BY_ID.get(email.applicant_id)?.stage ===
    APPLICANT_STAGE.pool
  )
}

function toMockOverviewMaskingDefectThreads(emails) {
  return hasRentalPortalMockState(MOCK_OVERVIEW_MASKING_DEFECT_STATE)
    ? [
        ...emails.filter(getIsMockOverviewPoolSender),
        ...emails.filter(email => !getIsMockOverviewPoolSender(email)),
      ]
    : emails
}

function getIsMockOverviewViewerThread(email) {
  return (
    email.from?.id === MOCK_OVERVIEW_SESSION_USER_ID ||
    (email.to ?? EMPTY_LIST).some(
      recipient => recipient.id === MOCK_OVERVIEW_SESSION_USER_ID
    )
  )
}

// TODO: §11 types `thread_id` as an int while the shared email fixture keys a
// thread by its `thr-<listing>-<applicant>` string — the fixture's id is served
// verbatim, so `Read` stays disabled until the endpoint serves the int.
function toMockOverviewCommunicationRow({ email, index, scope }) {
  const listing = scope.listingById.get(
    MOCK_OVERVIEW_APPLICANT_BY_ID.get(email.applicant_id)?.listing_id
  )

  return {
    flat: listing?.flat ?? null,
    listing_id: listing?.id ?? null,
    received_at: email.sent_at,
    sender_color: getRentalPortalMockAvatarColor(email.from?.id),
    sender_display: email.from?.full_name,
    sort_rank: index + 1,
    subject: email.subject,
    thread_id: email.thread_id,
  }
}

function getMockOverviewCommunications(scope) {
  const threads = new Map()

  EMAILS_SNAPSHOT.filter(
    email =>
      email.direction === MOCK_OVERVIEW_INBOUND_DIRECTION &&
      !email.is_read &&
      getIsMockOverviewViewerThread(email)
  )
    .map(email => ({
      applicant: MOCK_OVERVIEW_APPLICANT_BY_ID.get(email.applicant_id),
      email,
    }))
    .filter(
      ({ applicant }) =>
        applicant &&
        !applicant.archived &&
        scope.listingById.has(applicant.listing_id) &&
        getIsMockOverviewRevealedSender(applicant)
    )
    .sort(({ email: left }, { email: right }) =>
      right.sent_at.localeCompare(left.sent_at)
    )
    .forEach(({ email }) => {
      if (!threads.has(email.thread_id)) {
        threads.set(email.thread_id, email)
      }
    })

  return toMockOverviewPanel({
    rows: toMockOverviewMaskingDefectThreads([...threads.values()]),
    toRow: (email, index) =>
      toMockOverviewCommunicationRow({ email, index, scope }),
  })
}

function toMockOverviewInitials(label) {
  const words = label.split(' ').filter(Boolean)

  return [words.at(0), words.at(-1)]
    .map(word => word.at(0).toUpperCase())
    .join(EMPTY_STRING)
}

function getMockOverviewViewingApplicantDisplay(viewing) {
  if (hasRentalPortalMockState(MOCK_OVERVIEW_MASKING_DEFECT_STATE)) {
    return viewing.applicant_label
  }

  return viewing.applicant?.masked || !viewing.applicant_label
    ? MOCK_OVERVIEW_MASKED_APPLICANT_DISPLAY
    : toMockOverviewInitials(viewing.applicant_label)
}

function getMockOverviewUpcomingViewings({ clock, scope }) {
  return toMockOverviewPanel({
    rows: getMockOverviewScopeViewings(scope)
      .filter(
        viewing =>
          viewing.status === RENTAL_PORTAL_VIEWING_STATUS.confirmed &&
          getMockOverviewViewingDate(viewing) >= clock.now
      )
      .sort(
        (left, right) =>
          getMockOverviewViewingDate(left) -
            getMockOverviewViewingDate(right) || left.id - right.id
      ),
    toRow: viewing => ({
      applicant_display: getMockOverviewViewingApplicantDisplay(viewing),
      flat: scope.listingById.get(viewing.listing_id)?.flat,
      scheduled_at: viewing.start,
      viewing_id: viewing.id,
    }),
  })
}

// TODO: §11 declares no per-panel error envelope, so a panel whose feed failed
// is served as an absent key — the surface renders that panel's inline error
// and the other three plus the KPI row from the same response.
// TODO: §11 echoes `period` but not the window it resolved to, nor the scope it
// aggregated — so a KPI cannot link to the list that produced it without the FE
// re-deriving both, which is the server's to own (G4/BR-RP08-02). The four
// window bounds and `scope_user_id` are owed by the endpoint.
function toMockReportingOverview({ clock, period, scope }) {
  const interval = toMockOverviewPeriodInterval(period, clock)
  const week = toMockOverviewWeekInterval(clock)

  const awaitingApproval = getMockOverviewAwaitingApproval(scope)
  const communications = getMockOverviewCommunications(scope)
  const needsAttention = getMockOverviewNeedsAttention({ clock, scope })
  const upcomingViewings = getMockOverviewUpcomingViewings({ clock, scope })

  const overview = {
    awaiting_approval: awaitingApproval.rows,
    awaiting_approval_total: awaitingApproval.total,
    communications: communications.rows,
    communications_total: communications.total,
    has_scope: getMockOverviewHasScope(),
    kpis: getMockOverviewKpis({ clock, period, scope }),
    kpis_delta: getMockOverviewKpiDelta(),
    needs_attention: needsAttention.rows,
    needs_attention_total: needsAttention.total,
    period,
    period_end: format(interval.end, DATE_API_DATA_FORMAT_SHORT),
    period_start: format(interval.start, DATE_API_DATA_FORMAT_SHORT),
    scope_user_id: MOCK_OVERVIEW_SESSION_USER_ID,
    upcoming_viewings: upcomingViewings.rows,
    upcoming_viewings_total: upcomingViewings.total,
    week_end: format(week.end, DATE_API_DATA_FORMAT_SHORT),
    week_start: format(week.start, DATE_API_DATA_FORMAT_SHORT),
  }

  return hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.panelError)
    ? omit(overview, OVERVIEW_PAYLOAD_FIELD.upcomingViewings)
    : overview
}

function withMockOverviewLatency(isSlow) {
  return new Promise(resolve => {
    setTimeout(
      resolve,
      isSlow ? RENTAL_PORTAL_MOCK_LATENCY : MOCK_OVERVIEW_READ_LATENCY
    )
  })
}

const MOCK_OVERVIEW_SERVED_PERIODS = new Set()

function getIsMockOverviewRefresh(period) {
  const isRefresh = MOCK_OVERVIEW_SERVED_PERIODS.has(period)

  MOCK_OVERVIEW_SERVED_PERIODS.add(period)

  return isRefresh
}

function getIsMockOverviewReadError({ isRefresh, period }) {
  return (
    hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.error) ||
    (isRefresh &&
      hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.refreshError)) ||
    (period !== OVERVIEW_PERIOD_DEFAULT &&
      hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.periodError))
  )
}

export function getMockReportingOverview({ period }) {
  const servedPeriod = OVERVIEW_PERIOD_LIST.includes(period)
    ? period
    : OVERVIEW_PERIOD_DEFAULT
  const isRefresh = getIsMockOverviewRefresh(servedPeriod)

  return withMockOverviewLatency(
    hasRentalPortalMockState(
      isRefresh
        ? RENTAL_PORTAL_MOCK_STATE.slowRefresh
        : RENTAL_PORTAL_MOCK_STATE.loading
    )
  ).then(() =>
    getIsMockOverviewReadError({ isRefresh, period: servedPeriod })
      ? Promise.reject(MOCK_OVERVIEW_READ_ERROR)
      : {
          data: toMockReportingOverview({
            clock: toMockOverviewClock(),
            period: servedPeriod,
            scope: toMockOverviewScope(),
          }),
        }
  )
}
